import { sourceType } from './sourceType';

const folder = 'semiotic-file-icon__folder';
const image = 'semiotic-file-icon__image';
const audio = 'semiotic-file-icon__audio';
const archive = 'semiotic-file-icon__archive';
const presentation = 'semiotic-file-icon__presentation';
const spreadsheet = 'semiotic-file-icon__spreadsheet';
const video = 'semiotic-file-icon__video';
const doc = 'semiotic-file-icon__doc';
const pdf = 'semiotic-file-icon__pdf';
const text = 'semiotic-file-icon__text';
const writer = 'semiotic-file-icon__writer';
const impress = 'semiotic-file-icon__impress';
const calc = 'semiotic-file-icon__calc';
const googleDocument = 'semiotic-file-icon__google-document';
const googleSheets = 'semiotic-file-icon__google-sheets';
const googleSlides = 'semiotic-file-icon__google-slides';
const googleForms = 'semiotic-file-icon__google-forms';
const googleDrawings = 'semiotic-file-icon__google-drawings';

const generic = 'semiotic-file-icon__generic';
const genericGoogle = 'semiotic-file-icon__generic-google';
const genericOneDrive = 'semiotic-file-icon__generic-one-drive';
const genericOffice365 = 'semiotic-file-icon__generic-office365';

const extensionIcons = {
    // Image
    ai: image,
    bmp: image,
    gif: image,
    ico: image,
    jpeg: image,
    jpg: image,
    png: image,
    ps: image,
    psd: image,
    svg: image,
    tif: image,
    tiff: image,

    // Audio
    acc: audio,
    aif: audio,
    cda: audio,
    fla: audio,
    mid: audio,
    midi: audio,
    m4a: audio,
    mp3: audio,
    mpa: audio,
    ogg: audio,
    wav: audio,
    wma: audio,
    wpl: audio,

    // archives
    '7z': archive,
    arj: archive,
    deb: archive,
    pkg: archive,
    rar: archive,
    rpm: archive,
    tar: archive,
    z: archive,
    gz: archive,
    zip: archive,

    // Data file - no icon for data files so fall back to the next resolver
    // dat: generic,
    // db: generic,
    // mdb: generic,
    // sav: generic,

    // Executable - no icon for executable files so fall back to the next resolver
    // apk: generic,
    // bat: generic,
    // bin: generic,
    // com: generic,
    // exe: generic,
    // gadget: generic,
    // jar: generic,
    // wsf: generic,

    // Presentation
    key: presentation,
    odp: impress,
    pps: presentation,
    ppt: presentation,
    pptm: presentation,
    pptx: presentation,

    // Spreadsheet
    ods: calc,
    xlr: spreadsheet,
    xls: spreadsheet,
    xlsb: spreadsheet,
    xlsm: spreadsheet,
    xlsx: spreadsheet,

    '3g2': video,
    '3gp': video,
    avi: video,
    flv: video,
    h264: video,
    m4v: video,
    mkv: video,
    mov: video,
    mp4: video,
    mpg: video,
    mpeg: video,
    rm: video,
    swf: video,
    vob: video,
    webm: video,
    wmv: video,

    // Documents
    doc: doc,
    docm: doc,
    docx: doc,
    odt: writer,
    rtf: doc,
    wks: doc,
    wpd: doc,

    // PDF
    pdf: pdf,

    // Text files - no icon for text files so fall back to the next resolver
    // txt: text,
    // tex: text,
    // asp: text,
    // aspx: text,
    // cer: text,
    // cfm: text,
    // cgi: text,
    // pl: text,
    // css: text,
    // htm: text,
    // html: text,
    // js: text,
    // jsp: text,
    // part: text,
    // php: text,
    // py: text,
    // rss: text,
    // xhtml: text,
    // md: text,
    // c: text,
    // class: text,
    // cpp: text,
    // cs: text,
    // h: text,
    // java: text,
    // sh: text,
    // swift: text,
    // vb: text,
    // csv: text,
    // log: text,
    // sql: text,
    // xml: text,
};

// Currently we lack the icons for the commented mime types but they are left
// in place for when we create them or if we decide to use the non-cloud icons
const mimeTypeIcons = {
    // 'application/vnd.google-apps.audio'
    'application/vnd.google-apps.document': googleDocument,
    'application/vnd.google-apps.drawing': googleDrawings,
    'application/vnd.google-apps.file': genericGoogle,
    'application/vnd.google-apps.folder': folder,
    // 'application/vnd.google-apps.photo',
    'application/vnd.google-apps.presentation': googleSlides,
    // 'application/vnd.google-apps.script'
    'application/vnd.google-apps.forms': googleForms,
    'application/vnd.google-apps.spreadsheet': googleSheets,
    // 'application/vnd.google-apps.video'
};

// These are search terms for mime types of the 'application/*' type
const applicationMimeTypeSearches = {
    officedocument: doc,
    wordperfect: doc,
    msword: doc,
    wordpro: doc,
    kword: doc,
    text: doc,

    excel: spreadsheet,
    spreadsheet: spreadsheet,
    kspread: spreadsheet,

    powerpoint: presentation,
    presentation: presentation,
    kpresenter: presentation,

    'google-apps': genericGoogle,
};

const cloudProviderDefaults = {
    'gdrive': genericGoogle,
    'googledrive': genericGoogle,
    'google': genericGoogle,
    'onedrive': genericOneDrive,
    'onedriveforbusiness': genericOneDrive,
    'o365': genericOffice365,
    'office365': genericOffice365,
    // Also support the use of source type ids
    [sourceType.google]: genericGoogle,
    [sourceType.oneDriveForBusiness]: genericOneDrive,
    [sourceType.oneDrivePersonal]: genericOneDrive,
};

/**
 * @param {string} fileName [undefined] Name of file including extension if available
 * @param {string} mimeType [undefined] of the file if available
 * @param {string|number} cloudProvider [undefined] cloud provider's name or source id
 * @returns {string} css class for the icon from ./_fileIcons.scss
 */
export function iconClassForFile(fileName, mimeType, cloudProvider) {
    // Process everything as lowercase for simplicity
    if (fileName) {
        fileName = fileName.toLowerCase();
    }

    if (mimeType) {
        mimeType = mimeType.toLowerCase();
    }

    if (cloudProvider && typeof cloudProvider === 'string') {
        cloudProvider = cloudProvider.toLowerCase();
    }

    // First check for exact match for mime-type
    if (mimeType && mimeTypeIcons[mimeType]) {
        return mimeTypeIcons[mimeType];
    }

    // Check for an icon for the file extension
    if (fileName) {
        const extension = fileName.split('.').pop();

        if (extensionIcons[extension]) {
            return extensionIcons[extension];
        }
    }

    // Fallback to a fuzzy match on mimeType
    if (mimeType) {
        if (mimeType.includes('audio/')) {
            return audio;
        }

        if (mimeType.includes('video/')) {
            return video;
        }

        if (mimeType.includes('text/')) {
            return text;
        }

        if (mimeType.includes('image/')) {
            return image;
        }

        if (mimeType.includes('application/')) {
            const terms = Object.keys(applicationMimeTypeSearches);

            for (let i = 0; i < terms.length; i++) {
                if (mimeType.includes(terms[i])) {
                    return applicationMimeTypeSearches[terms[i]];
                }
            }
        }
    }

    // If a cloud provider is set use that cloud provider's default
    if (cloudProvider && cloudProviderDefaults[cloudProvider]) {
        return cloudProviderDefaults[cloudProvider];
    }

    // otherwise use a generic icon
    return generic;
}
